import { collectTreeEntries } from "../utils/tree.js";
import { safeColor } from "../../../../engine/core/validation.js";
import { GANTT_ZOOM_LEVELS } from "../constants.js";
import { rowShorterIcon, rowTallerIcon, targetIcon } from "../icons.js";
import { buildGanttModel } from "../utils/gantt.js";
import { escapeAttr, escapeHtml, trimText } from "../utils/html.js";
import { calendarDay } from "../../../../engine/core/calendar-day.js";
import { renderViewEmptyState } from "./empty.js";
import { t } from "../../../../shared/i18n/index.js";

const ZOOM_UNIT_KEYS = { week: "mobile.timeline.zoomWeeks", month: "mobile.timeline.zoomMonths", year: "mobile.timeline.zoomYears" };

// Rows drawn: those on screen and ROW_MARGIN above and below (all of them up
// to about 330 rows); the chart keeps its full height. Laying out every row
// of a 20,000-item map took 2.4 s when the timeline opened on a phone
// (docs/internal/perf/2026-10-08-big-maps.md). Scrolling redraws the rows in
// place (redrawGanttRows), keeping the scroller and its momentum.
const ROW_MARGIN = 150;
// Rows left beyond the screen at which the next ones are drawn.
const ROW_EDGE = 40;
let lastChart = null;

/** Rows [from, to) to draw for a scroll position. */
function rowWindow(chart, top, viewportHeight) {
  const total = chart.rows.length;
  const first = Math.max(0, Math.floor((top - chart.axisHeight) / chart.rowHeight));
  const last = Math.ceil((top + viewportHeight - chart.axisHeight) / chart.rowHeight);
  return { from: Math.min(total, Math.max(0, first - ROW_MARGIN)), to: Math.min(total, Math.max(0, last + ROW_MARGIN)), total };
}

/**
 * Draw the rows around the scroller's position when it nears an end of the
 * drawn ones. Returns true when it drew.
 */
export function redrawGanttRows(scroller, uiState) {
  const group = scroller?.querySelector(".mobile-gantt-rows");
  if (!lastChart || !group) return false;
  const drawn = uiState.ganttDrawnRows;
  const first = Math.floor((scroller.scrollTop - lastChart.axisHeight) / lastChart.rowHeight);
  const last = Math.ceil((scroller.scrollTop + scroller.clientHeight - lastChart.axisHeight) / lastChart.rowHeight);
  if (drawn && drawn.total === lastChart.rows.length
      && (drawn.from === 0 || first >= drawn.from + ROW_EDGE)
      && (drawn.to === drawn.total || last <= drawn.to - ROW_EDGE)) return false;
  const rows = rowWindow(lastChart, scroller.scrollTop, scroller.clientHeight);
  group.innerHTML = rowsMarkup(lastChart, rows);
  uiState.ganttDrawnRows = rows;
  return true;
}

/** "1w", "3m", "2y"… in the current language. */
function zoomLabel(level) {
  return t(ZOOM_UNIT_KEYS[level.unit], { n: level.n });
}

export function renderTimeline(appState, { callbacks, uiState, collectDatedEntries }) {
  const root = appState.data;
  if (!root) return renderViewEmptyState(t("mobile.empty.noDocument"), t("mobile.timeline.noDocText"));

  const horizon = callbacks.sharedCallbacks?.getHorizonPredicate?.();
  const planned = new Map(collectDatedEntries(root).map(entry => [entry.node.id, entry]));
  const tasks = collectTreeEntries(root).filter(({node}) => node.id !== root.id && (!horizon || !horizon(node)))
    .map(entry => {
      const returnDate = callbacks.sharedCallbacks?.getTaskPostponement?.(entry.node.id)?.until;
      const task = planned.get(entry.node.id);
      if (!task && !returnDate) return null;
      return { ...(task || { ...entry, startDay: calendarDay(returnDate), endDay: calendarDay(returnDate), returnOnly: true }), returnDate };
    }).filter(Boolean);

  return `
    <section class="mobile-view mobile-view-padded mobile-visual-view">
      <div data-horizon-host class="mobile-horizon-host"></div>
      ${renderGanttChart(tasks, uiState)}
    </section>
  `;
}

function renderGanttChart(entries, uiState) {
  const zoomIndex = Math.max(0, Math.min(GANTT_ZOOM_LEVELS.length - 1, uiState.ganttZoomIndex || 0));
  const zoom = GANTT_ZOOM_LEVELS[zoomIndex];
  const chart = buildGanttModel(entries, zoom.dayWidth, uiState.ganttRowHeight, {
    bounds: {
      startDay: Math.min(uiState.ganttBounds?.startDay ?? Infinity, ...entries.filter(e => e.returnDate).map(e => calendarDay(e.returnDate))),
      endDay: Math.max(uiState.ganttBounds?.endDay ?? -Infinity, ...entries.filter(e => e.returnDate).map(e => calendarDay(e.returnDate))),
    },
    viewportWidth: uiState.ganttViewportWidth || window.innerWidth,
    minHeight: Math.min(560, Math.max(240, window.innerHeight - 340)),
  });
  // The rows around where the chart will be scrolled: the task kept at the
  // center after a row height change, else the last scroll.
  const anchorIndex = uiState.ganttRowAnchor ? chart.rows.findIndex((row) => row.node.id === uiState.ganttRowAnchor.nodeId) : -1;
  const viewportHeight = window.innerHeight;
  const top = anchorIndex >= 0
    ? chart.axisHeight + (anchorIndex + uiState.ganttRowAnchor.fraction) * chart.rowHeight - viewportHeight / 2
    : uiState.ganttScrollTop || 0;
  const drawnRows = rowWindow(chart, top, viewportHeight);
  uiState.ganttDrawnRows = drawnRows;
  lastChart = chart;
  const todayDay = calendarDay(new Date());
  const todayX = chart.labelWidth + (todayDay - chart.startDay) * chart.dayWidth;
  const labelHidden = chart.dayWidth < 1.5;

  const toolbar = `
    <div class="mobile-visual-toolbar">
      <div class="mobile-visual-title">Gantt</div>
      <div class="mobile-visual-controls">
        <button class="mobile-icon-btn compact"
                data-mobile-action="gantt-row-zoom"
                data-zoom="out"
                aria-label="${escapeAttr(t("mobile.timeline.shorterRows"))}">${rowShorterIcon()}</button>
        <button class="mobile-icon-btn compact"
                data-mobile-action="gantt-row-zoom"
                data-zoom="in"
                aria-label="${escapeAttr(t("mobile.timeline.tallerRows"))}">${rowTallerIcon()}</button>
        <button class="mobile-icon-btn compact"
                data-mobile-action="gantt-zoom-today"
                aria-label="${escapeAttr(t("mobile.timeline.centerToday"))}">${targetIcon()}</button>
      </div>
    </div>
    <div class="mobile-gantt-zoom-bar">
      ${GANTT_ZOOM_LEVELS.map((level, idx) => `
        <button class="${idx === zoomIndex ? "active" : ""}"
                data-mobile-action="gantt-zoom-set"
                data-zoom-index="${idx}">${escapeHtml(zoomLabel(level))}</button>
      `).join("")}
    </div>
  `;

  return `
    <div class="mobile-visual-frame mobile-gantt-frame">
      ${toolbar}
      <div class="mobile-gantt-scroll" data-mobile-gantt-scroll
           data-start-day="${chart.startDay}" data-end-day="${chart.endDay}"
           data-row-height="${chart.rowHeight}" data-axis-height="${chart.axisHeight}"
           data-day-width="${chart.dayWidth}" data-label-width="${chart.labelWidth}">
        <svg class="mobile-gantt-svg ${labelHidden ? "compact" : ""}"
             width="${chart.width}"
             height="${chart.height}"
             viewBox="0 0 ${chart.width} ${chart.height}"
             role="img"
             aria-label="${escapeAttr(t("mobile.timeline.chart"))}">
          <rect width="${chart.width}" height="${chart.height}" rx="10" class="mobile-visual-bg" />
          <g class="mobile-gantt-axis">
            ${chart.ticks.map((tick) => `
              <line x1="${tick.x}" y1="0" x2="${tick.x}" y2="${chart.height}" class="${tick.unit}-boundary" data-date="${tick.date}" />
              ${tick.label ? `<text x="${tick.x + 4}" y="17">${escapeHtml(tick.label)}</text>` : ""}
              ${tick.sub ? `<text x="${tick.x + 4}" y="34" class="day">${escapeHtml(tick.sub)}</text>` : ""}
            `).join("")}
          </g>
          <g class="mobile-gantt-rows">${rowsMarkup(chart, drawnRows)}</g>
          ${todayX >= chart.labelWidth && todayX <= chart.width ? `<g class="mobile-gantt-today" aria-label="${escapeAttr(t("common.today"))}">
            <line x1="${todayX}" y1="0" x2="${todayX}" y2="${chart.height}" />
            <path d="M${todayX - 4} 0h8l-4 6z" /><title>${escapeHtml(t("common.today"))}</title>
          </g>` : ''}
        </svg>
      </div>
    </div>
  `;
}

/** The rows [from, to) of the chart, at their place in it. */
function rowsMarkup(chart, { from, to }) {
  return chart.rows.slice(from, to).map((row, offset) => {
    const index = from + offset;
    const y = chart.axisHeight + index * chart.rowHeight;
    const color = safeColor(row.node.color, "#06b6d4");
    const minBarWidth = Math.max(2, row.width);
    const barHeight = Math.max(8, chart.rowHeight - 14);
    const barY = y + (chart.rowHeight - barHeight) / 2;
    const labelY = y + chart.rowHeight / 2 + 4;
    const labelFontSize = Math.max(9, Math.min(14, chart.rowHeight * 0.32));
    const showBarLabel = !row.returnOnly && row.width > 30 && chart.rowHeight >= 26;
    const returnX = row.returnDate ? chart.labelWidth + (calendarDay(row.returnDate) - chart.startDay + 0.5) * chart.dayWidth : null;
    return `
      <g data-mobile-action="open-node-properties" data-node-id="${escapeAttr(row.node.id)}" data-row-index="${index}" class="${row.placeholder ? 'unscheduled' : ''}">
        ${row.placeholder ? `<title>${escapeHtml(t("mobile.timeline.unscheduled"))}</title>` : ''}
        <rect x="0" y="${y}" width="${chart.width}" height="${chart.rowHeight}" class="mobile-gantt-row-bg ${index % 2 ? "odd" : ""}" />
        <text x="${12 + row.depth * 9}" y="${labelY}" class="mobile-gantt-label" style="font-size:${labelFontSize}px">${escapeHtml(trimText(row.node.name || t("common.untitled"), 20))}</text>
        ${row.returnOnly ? '' : `<rect x="${row.x}" y="${barY}" width="${minBarWidth}" height="${barHeight}" rx="${Math.min(6, Math.min(barHeight, minBarWidth) / 2)}" fill="${escapeAttr(color)}" class="mobile-gantt-bar" />`}
        ${returnX === null ? '' : `<text x="${returnX}" y="${labelY}" text-anchor="middle" class="mobile-gantt-task-return">◆<title>${escapeHtml(t("views.gantt.returnToList", { date: row.returnDate }))}</title></text>`}
        ${showBarLabel ? `<text x="${row.x + 8}" y="${labelY}" class="mobile-gantt-bar-label" style="font-size:${labelFontSize - 1}px">${escapeHtml(trimText(row.node.name || t("common.untitled"), Math.max(4, Math.floor(row.width / 8))))}</text>` : ""}
      </g>
    `;
  }).join("");
}
